import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Pin, PinOff, Trash2, Pencil, Eye, Send, Copy, ExternalLink, Mail, Users, Newspaper, Settings2, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { bulletinAPI, toLocalInput, fromLocalInput, fmtWhen } from '@/lib/bulletinAPI';
import { safeFormat } from '@/lib/utils';
import { getCurrentUser } from '@/lib/auth';

const red = 'bg-[#A62B1F] hover:bg-[#8C2318] text-white';
const card = 'rounded-xl border border-[#E3DDD1] bg-white';
const serif = { fontFamily: 'Fraunces, Georgia, serif' };
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);

export const CATEGORIES = [
  ['announcement', 'Announcement'], ['event', 'Event'], ['volunteer', 'Volunteers'],
  ['prayer', 'Prayer'], ['news', 'News'], ['other', 'Other'],
];
const catLabel = (c) => (CATEGORIES.find(([k]) => k === c) || [c, c])[1];

const Tab = ({ id, cur, set, icon: Icon, children }) => (
  <button onClick={() => set(id)}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border ${cur === id ? 'bg-[#1E2A33] text-white border-[#1E2A33]' : 'text-[#5C6670] border-[#E3DDD1] bg-white'}`}>
    <Icon className="w-4 h-4" />{children}
  </button>
);

// ── Notices ──────────────────────────────────────────────────────────────────
const PostDialog = ({ open, onOpenChange, post, onSaved }) => {
  const blank = { title: '', body: '', category: 'announcement', status: 'published', pinned: false, event_date: '', expires_at: '' };
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(post ? { ...post, event_date: toLocalInput(post.event_date), expires_at: toLocalInput(post.expires_at) } : blank);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (status) => {
    if (!f.title.trim()) return toast.error('Give the notice a title');
    setSaving(true);
    const data = { title: f.title, body: f.body, category: f.category, status, pinned: f.pinned };
    if (f.event_date) data.event_date = fromLocalInput(f.event_date); else if (post) data.clear_event_date = true;
    if (f.expires_at) data.expires_at = fromLocalInput(f.expires_at); else if (post) data.clear_expires_at = true;
    try {
      if (post) await bulletinAPI.updatePost(post.id, data); else await bulletinAPI.createPost(data);
      toast.success(status === 'published' ? 'Notice is on the board' : 'Saved as a draft');
      onOpenChange(false); onSaved();
    } catch (e) { err(e, 'Could not save'); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{post ? 'Edit notice' : 'New notice'}</DialogTitle>
          <DialogDescription>Notices show on your public board and can be added to a newsletter.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Advent bake sale" /></div>
          <div><Label>Details</Label><Textarea rows={6} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Leave a blank line between paragraphs. Web links become clickable." /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Type</Label>
              <select className="w-full h-10 rounded-md border border-input px-2 text-sm bg-white" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
                {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div><Label>Event date (optional)</Label><Input type="datetime-local" value={f.event_date} onChange={(e) => setF({ ...f, event_date: e.target.value })} /></div>
          </div>
          <div><Label>Take down after (optional)</Label><Input type="datetime-local" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} />
            <p className="text-xs text-[#5C6670] mt-1">The notice leaves the board on its own at this time.</p></div>
          <div className="flex items-center justify-between rounded-lg border border-[#E3DDD1] p-3">
            <div><div className="text-sm font-semibold">Pin to the top</div><div className="text-xs text-[#5C6670]">For the most important notice.</div></div>
            <Switch checked={f.pinned} onCheckedChange={(v) => setF({ ...f, pinned: v })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" disabled={saving} onClick={() => save('draft')}>Save draft</Button>
            <Button className={red} disabled={saving} onClick={() => save('published')}>{saving ? 'Saving…' : 'Publish'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Notices = ({ posts, reload }) => {
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const now = Date.now();
  const state = (p) => (p.status === 'draft' ? ['Draft', 'bg-[#F3EFE7] text-[#5C6670]']
    : p.expires_at && new Date(p.expires_at).getTime() < now ? ['Taken down', 'bg-[#F3EFE7] text-[#5C6670]']
      : ['On the board', 'bg-emerald-50 text-emerald-800']);
  return (
    <div className={`${card} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-[#1E2A33]">Notices</h2>
        <Button size="sm" className={red} onClick={() => { setEdit(null); setOpen(true); }}><Plus className="w-4 h-4 mr-1" />New notice</Button>
      </div>
      {posts === null ? <p className="text-sm text-[#5C6670]">Loading…</p> : posts.length === 0 ? (
        <div className="text-center py-10 text-sm text-[#5C6670]"><Newspaper className="w-7 h-7 mx-auto mb-2 text-[#A62B1F]" />No notices yet. Post your first one for the board.</div>
      ) : (
        <ul className="divide-y divide-[#E3DDD1]">
          {posts.map((p) => {
            const [label, cls] = state(p);
            return (
              <li key={p.id} className="py-3 flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.pinned && <Pin className="w-3.5 h-3.5 text-[#A62B1F]" />}
                    <span className="font-semibold text-sm text-[#1E2A33]">{p.title}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
                    <span className="text-[11px] text-[#5C6670]">{catLabel(p.category)}</span>
                  </div>
                  {p.body && <p className="text-xs text-[#5C6670] mt-1 line-clamp-2">{p.body}</p>}
                  <div className="text-[11px] text-[#5C6670] mt-1">
                    {p.event_date && <>Event {fmtWhen(p.event_date)} · </>}
                    {p.expires_at && <>Comes down {fmtWhen(p.expires_at)} · </>}
                    Updated {safeFormat(p.updated_at, 'MMM d')}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" title={p.pinned ? 'Unpin' : 'Pin'} onClick={async () => { await bulletinAPI.updatePost(p.id, { pinned: !p.pinned }); reload(); }}>{p.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}</Button>
                  <Button size="sm" variant="ghost" title="Edit" onClick={() => { setEdit(p); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" title="Delete" className="text-[#A62B1F]" onClick={async () => { if (!window.confirm(`Delete “${p.title}”?`)) return; await bulletinAPI.removePost(p.id); toast.success('Deleted'); reload(); }}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <PostDialog open={open} onOpenChange={setOpen} post={edit} onSaved={reload} />
    </div>
  );
};

// ── Newsletters ──────────────────────────────────────────────────────────────
const IssueEditor = ({ issue, posts, onClose, onChanged, activeCount, addressReady }) => {
  const [f, setF] = useState({ subject: issue.subject, intro: issue.intro, closing: issue.closing, post_ids: issue.post_ids, include_signups: issue.include_signups });
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const sent = issue.status !== 'draft';
  const usable = useMemo(() => (posts || []).filter((p) => p.status === 'published'), [posts]);

  const save = async (quiet) => {
    try { await bulletinAPI.updateIssue(issue.id, f); if (!quiet) toast.success('Saved'); onChanged(); return true; } catch (e) { err(e, 'Could not save'); return false; }
  };
  const showPreview = async () => {
    if (!sent && !(await save(true))) return;
    const r = await bulletinAPI.previewIssue(issue.id);
    setPreview(r.data);
  };
  const toggle = (id) => setF({ ...f, post_ids: f.post_ids.includes(id) ? f.post_ids.filter((x) => x !== id) : [...f.post_ids, id] });

  return (
    <div className={`${card} p-4`}>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <button className="text-sm text-[#A62B1F] font-semibold" onClick={onClose}>← All newsletters</button>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={showPreview}><Eye className="w-4 h-4 mr-1" />Preview</Button>
          {!sent && <Button size="sm" variant="outline" disabled={busy} onClick={async () => { if (!(await save(true))) return; setBusy(true); try { const r = await bulletinAPI.testIssue(issue.id); toast.success(r.data.message); } catch (e) { err(e, 'Test failed'); } finally { setBusy(false); } }}><FlaskConical className="w-4 h-4 mr-1" />Send me a test</Button>}
          {!sent && <Button size="sm" variant="outline" onClick={() => save()}>Save</Button>}
          {!sent && <Button size="sm" className={red} disabled={busy} onClick={async () => {
            if (!addressReady) return toast.error('Add your mailing address in Configuration → Company first. The law requires it on every newsletter.');
            if (!activeCount) return toast.error('There are no confirmed subscribers yet.');
            if (!(await save(true))) return;
            if (!window.confirm(`Send “${f.subject}” to ${activeCount} subscriber${activeCount === 1 ? '' : 's'} now? This can't be undone.`)) return;
            setBusy(true);
            try { const r = await bulletinAPI.sendIssue(issue.id); toast.success(r.data.message); onChanged(); onClose(); } catch (e) { err(e, 'Could not send'); } finally { setBusy(false); }
          }}><Send className="w-4 h-4 mr-1" />Send</Button>}
        </div>
      </div>
      {sent && <p className="text-sm rounded-lg bg-[#FBF8F2] border border-[#E3DDD1] p-3 mb-3">This newsletter went out {safeFormat(issue.sent_at, 'MMM d, yyyy h:mm a')}. Duplicate it to send a new one.</p>}
      <fieldset disabled={sent} className="space-y-3">
        <div><Label>Subject line</Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} /></div>
        <div><Label>Opening message</Label><Textarea rows={5} value={f.intro} onChange={(e) => setF({ ...f, intro: e.target.value })} placeholder="A warm hello, the week ahead, a thought for the week…" /></div>
        <div>
          <Label>Notices to include</Label>
          {usable.length === 0 ? <p className="text-xs text-[#5C6670] mt-1">Publish some notices first and they'll show here.</p> : (
            <div className="mt-1 space-y-1 max-h-64 overflow-y-auto rounded-lg border border-[#E3DDD1] p-2">
              {usable.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                  <input type="checkbox" checked={f.post_ids.includes(p.id)} onChange={() => toggle(p.id)} />
                  <span className="font-medium">{p.title}</span>
                  <span className="text-xs text-[#5C6670]">{catLabel(p.category)}{p.event_date ? ` · ${fmtWhen(p.event_date)}` : ''}</span>
                </label>
              ))}
            </div>
          )}
          <p className="text-xs text-[#5C6670] mt-1">They appear in the order you tick them.</p>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.include_signups} onChange={(e) => setF({ ...f, include_signups: e.target.checked })} />Add a “Volunteers needed” list of open sign-ups</label>
        <div><Label>Closing (optional)</Label><Textarea rows={3} value={f.closing} onChange={(e) => setF({ ...f, closing: e.target.value })} placeholder="Blessings, and see you Sunday." /></div>
      </fieldset>
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview('')}>
        <DialogContent className="max-w-3xl h-[85vh] p-0 overflow-hidden">
          <iframe title="Newsletter preview" srcDoc={preview} className="w-full h-full border-0" sandbox="" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Newsletters = ({ posts, counts, addressReady, title, allowance }) => {
  const [issues, setIssues] = useState(null);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => bulletinAPI.issues().then((r) => setIssues(r.data)).catch(() => setIssues([])), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { // refresh while one is sending
    if (!issues?.some((i) => i.status === 'sending')) return undefined;
    const t = setInterval(load, 4000); return () => clearInterval(t);
  }, [issues, load]);
  const current = issues?.find((i) => i.id === openId);
  if (current) return <IssueEditor issue={current} posts={posts} onClose={() => setOpenId(null)} onChanged={load} activeCount={counts.active || 0} addressReady={addressReady} />;

  return (
    <div className={`${card} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-semibold text-[#1E2A33]">Newsletters</h2>
          <p className="text-xs text-[#5C6670]">{counts.active || 0} confirmed subscriber{counts.active === 1 ? '' : 's'}{allowance ? ` · ${allowance.used.toLocaleString()} of ${allowance.limit.toLocaleString()} emails used this month` : ''}</p>
        </div>
        <Button size="sm" className={red} onClick={async () => {
          const published = (posts || []).filter((p) => p.status === 'published' && !(p.expires_at && new Date(p.expires_at) < new Date()));
          try {
            const r = await bulletinAPI.createIssue({ subject: `${title ? `${title}: ` : ''}news for the week of ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`,
              intro: '', post_ids: published.slice(0, 6).map((p) => p.id), include_signups: true });
            await load(); setOpenId(r.data.id);
          } catch (e) { err(e, 'Could not start'); }
        }}><Plus className="w-4 h-4 mr-1" />New newsletter</Button>
      </div>
      {!addressReady && <p className="text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-900 p-2 mb-3">Before you can send, add your mailing address in Configuration → Company. It goes at the bottom of every newsletter, as the law requires.</p>}
      {issues === null ? <p className="text-sm text-[#5C6670]">Loading…</p> : issues.length === 0 ? (
        <div className="text-center py-10 text-sm text-[#5C6670]"><Mail className="w-7 h-7 mx-auto mb-2 text-[#A62B1F]" />No newsletters yet. Start one and pick the notices to include.</div>
      ) : (
        <ul className="divide-y divide-[#E3DDD1]">
          {issues.map((i) => (
            <li key={i.id} className="py-3 flex items-center gap-3">
              <button className="min-w-0 flex-1 text-left" onClick={() => setOpenId(i.id)}>
                <div className="font-semibold text-sm text-[#1E2A33] truncate">{i.subject}</div>
                <div className="text-xs text-[#5C6670]">
                  {i.status === 'draft' && `Draft · ${i.post_ids.length} notice${i.post_ids.length === 1 ? '' : 's'}`}
                  {i.status === 'sending' && 'Sending now…'}
                  {i.status === 'sent' && `Sent ${safeFormat(i.sent_at, 'MMM d, yyyy')} to ${i.stats?.sent ?? 0}${i.stats?.failed ? ` · ${i.stats.failed} failed` : ''}`}
                </div>
              </button>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="ghost" title="Duplicate" onClick={async () => { const r = await bulletinAPI.duplicateIssue(i.id); await load(); setOpenId(r.data.id); }}><Copy className="w-4 h-4" /></Button>
                {i.status !== 'sending' && <Button size="sm" variant="ghost" title="Delete" className="text-[#A62B1F]" onClick={async () => { if (!window.confirm('Delete this newsletter?')) return; await bulletinAPI.removeIssue(i.id); load(); }}><Trash2 className="w-4 h-4" /></Button>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── Subscribers ──────────────────────────────────────────────────────────────
const Subscribers = ({ data, reload }) => {
  const [one, setOne] = useState({ name: '', email: '' });
  const [bulk, setBulk] = useState('');
  const [consent, setConsent] = useState(false);
  const [q, setQ] = useState('');
  const [showBulk, setShowBulk] = useState(false);
  const rows = (data?.subscribers || []).filter((s) => !q || `${s.name || ''} ${s.email}`.toLowerCase().includes(q.toLowerCase()));
  const badge = { active: ['Subscribed', 'bg-emerald-50 text-emerald-800'], pending: ['Waiting to confirm', 'bg-amber-50 text-amber-800'], unsubscribed: ['Unsubscribed', 'bg-[#F3EFE7] text-[#5C6670]'] };

  return (
    <div className="grid md:grid-cols-[1fr_320px] gap-4">
      <div className={`${card} p-4`}>
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <h2 className="font-semibold text-[#1E2A33]">Subscribers</h2>
          <div className="text-xs text-[#5C6670]">{data?.counts?.active || 0} subscribed · {data?.counts?.pending || 0} waiting · {data?.counts?.unsubscribed || 0} unsubscribed</div>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="mb-2 h-9" />
        {!data ? <p className="text-sm text-[#5C6670]">Loading…</p> : rows.length === 0 ? (
          <div className="text-center py-10 text-sm text-[#5C6670]"><Users className="w-7 h-7 mx-auto mb-2 text-[#A62B1F]" />No subscribers yet. People can join from your public board, or you can add them here.</div>
        ) : (
          <ul className="divide-y divide-[#E3DDD1] max-h-[60vh] overflow-y-auto">
            {rows.map((s) => (
              <li key={s.id} className="py-2 flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{s.name || s.email}</div>
                  {s.name && <div className="text-xs text-[#5C6670] truncate">{s.email}</div>}
                </div>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badge[s.status]?.[1]}`}>{badge[s.status]?.[0]}</span>
                <span className="text-[11px] text-[#5C6670] w-20 text-right hidden sm:block">{({ board: 'Joined online', signup: 'Via sign-up', team: 'Added by team' })[s.source] || ''}</span>
                <Button size="sm" variant="ghost" className="text-[#A62B1F]" title="Remove" onClick={async () => { if (!window.confirm(`Remove ${s.email}?`)) return; await bulletinAPI.removeSubscriber(s.id); reload(); }}><Trash2 className="w-4 h-4" /></Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className={`${card} p-4 space-y-3 h-fit`}>
        <h3 className="font-semibold text-sm">Add people</h3>
        <p className="text-xs text-[#5C6670]">Only add people who asked to get the newsletter, like on a paper sign-up sheet. Anyone can unsubscribe from any email.</p>
        {!showBulk ? (
          <>
            <Input placeholder="Name" value={one.name} onChange={(e) => setOne({ ...one, name: e.target.value })} />
            <Input placeholder="Email" value={one.email} onChange={(e) => setOne({ ...one, email: e.target.value })} />
          </>
        ) : (
          <Textarea rows={7} value={bulk} onChange={(e) => setBulk(e.target.value)} placeholder={'One person per line:\nMary Smith, mary@example.com\njohn@example.com'} />
        )}
        <label className="flex items-start gap-2 text-xs"><input type="checkbox" className="mt-0.5" checked={consent} onChange={(e) => setConsent(e.target.checked)} />{showBulk ? 'Everyone on this list asked to receive the newsletter' : 'This person asked to receive the newsletter'}</label>
        <Button className={`w-full ${red}`} onClick={async () => {
          try {
            if (showBulk) {
              const r = await bulletinAPI.bulkSubscribers({ lines: bulk, consent_confirmed: consent });
              const d = r.data;
              toast.success(`${d.added + d.reactivated} added${d.exists ? ` · ${d.exists} already on the list` : ''}${d.suppressed ? ` · ${d.suppressed} had unsubscribed` : ''}${d.invalid ? ` · ${d.invalid} lines skipped` : ''}`);
              setBulk('');
            } else {
              await bulletinAPI.addSubscriber({ ...one, consent_confirmed: consent });
              toast.success('Added'); setOne({ name: '', email: '' });
            }
            setConsent(false); reload();
          } catch (e) { err(e, 'Could not add'); }
        }}>Add</Button>
        <button className="text-xs font-semibold text-[#A62B1F]" onClick={() => setShowBulk(!showBulk)}>{showBulk ? 'Add one person' : 'Paste a list instead'}</button>
      </div>
    </div>
  );
};

// ── Board settings ───────────────────────────────────────────────────────────
const BoardSettings = ({ settings, reload }) => {
  const [f, setF] = useState(settings);
  useEffect(() => setF(settings), [settings]);
  const me = getCurrentUser();
  const admin = ['owner', 'admin'].includes(me?.role) || me?.is_super_admin;
  if (!f) return null;
  const save = async (patch) => {
    try { await bulletinAPI.updateSettings(patch); toast.success('Saved'); reload(); } catch (e) { err(e, 'Could not save'); }
  };
  return (
    <div className={`${card} p-4 space-y-4 max-w-2xl`}>
      <div className="flex items-center justify-between rounded-lg border border-[#E3DDD1] p-3">
        <div>
          <div className="text-sm font-semibold">Public notice board</div>
          <div className="text-xs text-[#5C6670]">When this is on, anyone with the link can read your published notices, sign up to volunteer and subscribe. No login needed.</div>
        </div>
        <Switch checked={f.board_public} disabled={!admin} onCheckedChange={(v) => save({ board_public: v })} />
      </div>
      {f.board_public && (
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <code className="px-2 py-1 rounded bg-[#FBF8F2] border border-[#E3DDD1] text-xs break-all">{f.board_url}</code>
          <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(f.board_url); toast.success('Link copied'); }}><Copy className="w-4 h-4 mr-1" />Copy</Button>
          <Button size="sm" variant="outline" onClick={() => window.open(f.board_url, '_blank')}><ExternalLink className="w-4 h-4 mr-1" />Open</Button>
        </div>
      )}
      <div><Label>Board name</Label><Input value={f.board_title || ''} disabled={!admin} onChange={(e) => setF({ ...f, board_title: e.target.value })} placeholder={f.company_name} /></div>
      <div><Label>Welcome message</Label><Textarea rows={3} value={f.board_intro || ''} disabled={!admin} onChange={(e) => setF({ ...f, board_intro: e.target.value })} placeholder="Welcome! Here's what's happening at the church this month." /></div>
      <div><Label>Time zone for emails</Label><Input value={f.timezone || ''} disabled={!admin} onChange={(e) => setF({ ...f, timezone: e.target.value })} placeholder="America/Toronto" /></div>
      {admin ? <Button className={red} onClick={() => save({ board_title: f.board_title, board_intro: f.board_intro, timezone: f.timezone })}>Save</Button>
        : <p className="text-xs text-[#5C6670]">Only an admin can change these.</p>}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
export default function Bulletin() {
  const [tab, setTab] = useState('notices');
  const [posts, setPosts] = useState(null);
  const [subs, setSubs] = useState(null);
  const [settings, setSettings] = useState(null);
  const loadPosts = useCallback(() => bulletinAPI.posts().then((r) => setPosts(r.data)).catch(() => setPosts([])), []);
  const loadSubs = useCallback(() => bulletinAPI.subscribers().then((r) => setSubs(r.data)).catch(() => setSubs({ counts: {}, subscribers: [] })), []);
  const loadSettings = useCallback(() => bulletinAPI.settings().then((r) => setSettings(r.data)).catch(() => {}), []);
  useEffect(() => { loadPosts(); loadSubs(); loadSettings(); }, [loadPosts, loadSubs, loadSettings]);

  return (
    <Layout title="Bulletin">
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-[#1E2A33]" style={serif}>Bulletin</h1>
            <p className="text-sm text-[#5C6670]">Post notices to your board and send them out as a newsletter.</p>
          </div>
          {settings?.board_public && <Button variant="outline" size="sm" onClick={() => window.open(settings.board_url, '_blank')}><ExternalLink className="w-4 h-4 mr-1" />View the public board</Button>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Tab id="notices" cur={tab} set={setTab} icon={Newspaper}>Notices</Tab>
          <Tab id="newsletters" cur={tab} set={setTab} icon={Mail}>Newsletters</Tab>
          <Tab id="subscribers" cur={tab} set={setTab} icon={Users}>Subscribers</Tab>
          <Tab id="settings" cur={tab} set={setTab} icon={Settings2}>Board settings</Tab>
        </div>
        {tab === 'notices' && <Notices posts={posts} reload={loadPosts} />}
        {tab === 'newsletters' && <Newsletters posts={posts} counts={subs?.counts || {}} addressReady={!!settings?.address_ready} title={settings?.board_title} allowance={settings?.newsletter_allowance} />}
        {tab === 'subscribers' && <Subscribers data={subs} reload={loadSubs} />}
        {tab === 'settings' && <BoardSettings settings={settings} reload={loadSettings} />}
      </div>
    </Layout>
  );
}
